import React, { useCallback, useEffect, useState } from 'react';
import { Form, Modal, Table } from 'react-bootstrap';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { CENTRAL_ROLES } from '../pharmacyCopy';
import { useAuth } from '../../../context/AuthContext';
import Icon from '../../common/Icons';
import { PharmacyPageHeader, LoadingBlock, EmptyBlock, SeverityBadge } from '../ui';

const EMPTY = { medication_a_id: '', medication_b_id: '', severity: 'MODERATE', description: '' };

export default function PharmacyInteractionsPage() {
  const { toast } = usePharmacy();
  const { user } = useAuth();
  const canManage = CENTRAL_ROLES.includes(user?.role);

  const [rows, setRows] = useState([]);
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pharmacyService.listInteractions({ severity: severityFilter || undefined });
      setRows(res.success ? res.data : []);
    } finally {
      setLoading(false);
    }
  }, [severityFilter]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    pharmacyService.listMedications({ status: 'ACTIVE' }).then((r) => setMedications(r.success ? r.data : []));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const res =
        editing === 'new'
          ? await pharmacyService.createInteraction(form)
          : await pharmacyService.updateInteraction(editing, { severity: form.severity, description: form.description });
      if (res.success) {
        toast('success', res.message || 'Enregistré.');
        setEditing(null);
        await load();
      } else {
        toast('danger', res.message || 'Échec.');
      }
    } catch (e) {
      toast('danger', e.message || 'Échec.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (i) => {
    if (!window.confirm('Supprimer cette interaction ?')) return;
    const res = await pharmacyService.deleteInteraction(i.id);
    if (res.success) {
      toast('success', 'Supprimée.');
      await load();
    }
  };

  return (
    <div>
      <PharmacyPageHeader
        badge="Interactions"
        title="Référentiel d’interactions médicamenteuses"
        description="Paires de médicaments et sévérité. Une interaction majeure bloque la dispensation (dérogation possible)."
        actions={
          canManage ? (
            <button
              type="button"
              className="cp-btn cp-btn--primary cp-btn--sm"
              onClick={() => {
                setForm(EMPTY);
                setEditing('new');
              }}
            >
              <Icon name="plus" size={14} aria-hidden />
              Nouvelle interaction
            </button>
          ) : null
        }
      />

      <div className="cp-toolbar">
        {['', 'MAJOR', 'MODERATE', 'MINOR'].map((s) => (
          <button key={s || 'all'} type="button" className={`cp-filter-chip ${severityFilter === s ? 'is-active' : ''}`} onClick={() => setSeverityFilter(s)}>
            {s === '' ? 'Toutes' : s}
          </button>
        ))}
      </div>

      <div className="cp-card">
        <div className="cp-card__body cp-card__body--flush">
          {loading ? (
            <LoadingBlock />
          ) : rows.length === 0 ? (
            <EmptyBlock label="Aucune interaction enregistrée." />
          ) : (
            <Table responsive hover className="cp-table">
              <thead>
                <tr>
                  <th>Médicament A</th>
                  <th>Médicament B</th>
                  <th>Sévérité</th>
                  <th>Description</th>
                  {canManage && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map((i) => (
                  <tr key={i.id}>
                    <td>{i.medication_a_name}</td>
                    <td>{i.medication_b_name}</td>
                    <td><SeverityBadge severity={i.severity} /></td>
                    <td className="small">{i.description || '—'}</td>
                    {canManage && (
                      <td className="text-end">
                        <div className="cp-row-actions">
                          <button
                            type="button"
                            className="cp-btn cp-btn--ghost cp-btn--sm"
                            onClick={() => {
                              setForm({ ...EMPTY, ...i });
                              setEditing(i.id);
                            }}
                          >
                            Modifier
                          </button>
                          <button type="button" className="cp-btn cp-btn--danger cp-btn--sm" onClick={() => remove(i)}>
                            Suppr.
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      <Modal show={!!editing} onHide={() => setEditing(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">{editing === 'new' ? 'Nouvelle interaction' : 'Modifier l’interaction'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editing === 'new' && (
            <div className="row g-2 mb-2">
              <div className="col-6">
                <Form.Label>Médicament A</Form.Label>
                <Form.Select value={String(form.medication_a_id || '')} onChange={(e) => setForm({ ...form, medication_a_id: e.target.value })}>
                  <option value="">— Choisir —</option>
                  {medications.map((m) => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
                </Form.Select>
              </div>
              <div className="col-6">
                <Form.Label>Médicament B</Form.Label>
                <Form.Select value={String(form.medication_b_id || '')} onChange={(e) => setForm({ ...form, medication_b_id: e.target.value })}>
                  <option value="">— Choisir —</option>
                  {medications.map((m) => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
                </Form.Select>
              </div>
            </div>
          )}
          <Form.Group className="mb-2" style={{ maxWidth: 220 }}>
            <Form.Label>Sévérité</Form.Label>
            <Form.Select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
              <option value="MINOR">Mineure</option>
              <option value="MODERATE">Modérée</option>
              <option value="MAJOR">Majeure (bloquante)</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control as="textarea" rows={3} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="cp-modal__footer">
          <button type="button" className="cp-btn cp-btn--ghost" onClick={() => setEditing(null)} disabled={busy}>
            Annuler
          </button>
          <button
            type="button"
            className="cp-btn cp-btn--primary"
            onClick={save}
            disabled={
              busy ||
              (editing === 'new' &&
                (!form.medication_a_id || !form.medication_b_id || form.medication_a_id === form.medication_b_id))
            }
          >
            Enregistrer
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
